<template>
  <div class="bg-light min-vh-100">
    <main class="container">
      <div class="row justify-content-center">
        <div class="col-lg-8">
          <div class="card shadow-sm">
            <div class="card-body">
              <h1 class="h4 mb-2">Batch Create Devices</h1>
              <p class="help mb-3">
                Select a device model and template, then scan serial numbers to create orders rapidly.
              </p>

              <!-- Device Model -->
              <div class="mb-3">
                <label class="form-label">Device Model</label>
                <select v-model="selectedDeviceModel" class="form-select" @change="onDeviceModelSelect">
                  <option disabled :value="null">Select a device model...</option>
                  <option v-for="dm in deviceModels" :key="dm.device_model_id" :value="dm.device_model_id">
                    {{ dm.make }} {{ dm.model_name }} {{ dm.common_name ? '(' + dm.common_name + ')' : '' }}
                  </option>
                </select>
              </div>

              <!-- Template -->
              <div class="mb-3">
                <label class="form-label">Template</label>
                <select v-model="selectedTemplate" class="form-select">
                  <option disabled value="">Select template...</option>
                  <option v-for="t in templates" :key="t.template_id" :value="t.template_id">
                    {{ t.template_name }} (v{{ t.template_version }})
                  </option>
                </select>
              </div>

              <!-- Serial input -->
              <div class="mb-3" v-if="selectedDeviceModel && selectedTemplate">
                <label class="form-label">Serial Number</label>
                <div class="input-group">
                  <input
                    ref="serialInput"
                    v-model="serialNumber"
                    type="text"
                    class="form-control"
                    placeholder="Scan or type serial number, press Enter"
                    @keydown.enter.prevent="createOne"
                    :disabled="creating"
                  />
                  <button class="btn btn-primary" @click="createOne" :disabled="!serialNumber.trim() || creating">
                    <span v-if="creating" class="spinner-border spinner-border-sm me-1"></span>
                    Add
                  </button>
                </div>
              </div>

              <!-- Results -->
              <div v-if="results.length" class="mt-3">
                <h2 class="h6 mb-2">Created ({{ successCount }}/{{ results.length }})</h2>
                <table class="table table-sm align-middle">
                  <thead class="table-light">
                    <tr>
                      <th style="width: 40px;"></th>
                      <th>Serial</th>
                      <th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(r, i) in results" :key="i">
                      <td>
                        <span v-if="r.success" class="text-success">&#10003;</span>
                        <span v-else class="text-danger">&#10007;</span>
                      </td>
                      <td>{{ r.serial }}</td>
                      <td>
                        <span v-if="r.success">
                          Order #{{ r.orderId }}
                        </span>
                        <span v-else class="text-danger">{{ r.error }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Actions -->
              <div class="d-flex gap-2 mt-4">
                <button class="btn btn-outline-secondary" @click="$router.push({ name: 'OrdersList' })">
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { APIService } from "../http/APIService";

export default {
  name: "BatchCreate",
  data() {
    return {
      api: new APIService(),
      deviceModels: [],
      templates: [],
      selectedDeviceModel: null,
      selectedTemplate: "",
      serialNumber: "",
      creating: false,
      results: [],
      // Cached device model fields
      deviceFields: {
        device_make: "",
        device_model: "",
        device_commonname: "",
        device_type: "",
        device_model_ref: null,
      },
    };
  },
  computed: {
    successCount() {
      return this.results.filter((r) => r.success).length;
    },
  },
  created() {
    this.fetchDeviceModels();
    this.fetchTemplates();
  },
  methods: {
    async fetchDeviceModels() {
      try {
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.getDeviceModels(accessToken);
        this.deviceModels = res.data || [];
      } catch {
        // silent
      }
    },

    async fetchTemplates() {
      try {
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.getTemplates(accessToken);
        this.templates = res.data.filter((t) => t.active_template);
        if (this.templates.length === 1) {
          this.selectedTemplate = this.templates[0].template_id;
        }
      } catch {
        // silent
      }
    },

    onDeviceModelSelect() {
      if (!this.selectedDeviceModel) {
        this.deviceFields = { device_make: "", device_model: "", device_commonname: "", device_type: "", device_model_ref: null };
        return;
      }
      const dm = this.deviceModels.find((m) => m.device_model_id === this.selectedDeviceModel);
      if (dm) {
        this.deviceFields = {
          device_make: dm.make,
          device_model: dm.model_name,
          device_commonname: dm.common_name || "",
          device_type: dm.device_type || "Laptop",
          device_model_ref: dm.device_model_id,
        };
      }
      this.$nextTick(() => {
        if (this.$refs.serialInput) this.$refs.serialInput.focus();
      });
    },

    async createOne() {
      const serial = this.serialNumber.trim();
      if (!serial) return;

      this.creating = true;
      try {
        const accessToken = localStorage.getItem("accessToken");
        const payload = {
          serial_number: serial,
          template: this.selectedTemplate,
          ...this.deviceFields,
        };
        const res = await this.api.createOrder(payload, accessToken);
        this.results.unshift({ success: true, serial, orderId: res.data.order_id });
      } catch (err) {
        const detail = err.response?.data?.serial_number || err.response?.data?.detail || "Creation failed";
        const msg = Array.isArray(detail) ? detail.join(", ") : String(detail);
        this.results.unshift({ success: false, serial, error: msg });
      } finally {
        this.creating = false;
        this.serialNumber = "";
        this.$nextTick(() => {
          if (this.$refs.serialInput) this.$refs.serialInput.focus();
        });
      }
    },
  },
};
</script>
